/**
 * Food Module TypeScript type definitions
 *
 * Re-exports generated types from Typelizer and adds page props/utilities.
 * The serializer types are the source of truth - do not duplicate them here.
 */

// =============================================================================
// Re-export Generated Types from Typelizer
// =============================================================================

export type { default as FoodTag } from './serializers/ApiFoodTag';
export type { default as Ingredient } from './serializers/ApiFoodIngredient';
export type { default as IngredientWithCount } from './serializers/ApiFoodIngredientList';
export type { default as RecipeIngredient } from './serializers/ApiFoodRecipeIngredient';
export type { default as RecipeRating } from './serializers/ApiFoodRecipeRating';
export type { default as RecipePrep } from './serializers/ApiFoodRecipePrep';
export type { default as FamilyMember } from './serializers/ApiFoodFamilyMember';
export type { default as RecipeSummary } from './serializers/ApiFoodRecipe';
export type { default as RecipeDetail } from './serializers/ApiFoodRecipeDetail';

// =============================================================================
// Additional Types (not generated)
// =============================================================================

/**
 * Ingredient substitute reference
 */
export interface IngredientSubstitute {
  id: number;
  name: string;
}

/**
 * Recipe reference for ingredient detail sidebar
 */
export interface RecipeReference {
  id: number;
  title: string;
}

/**
 * Ingredient with full details for the detail sidebar
 */
export interface IngredientWithDetails {
  id: number;
  name: string;
  category: string | null;
  defaultUnit: string | null;
  recipeCount: number;
  substitutes: Array<{ id: number; name: string }>;
  recipes?: RecipeReference[];
}

// =============================================================================
// Filter Types
// =============================================================================

/**
 * Filter state for recipe list
 */
export interface RecipeFilters {
  search?: string;
  tag_id?: string;
  tag_ids?: string[];
  max_time?: string;
  freezable?: string;
  eat_immediately?: string;
  sort?: string;
}

/**
 * Sort options for recipe list
 */
export type RecipeSortOption =
  | 'recently_updated'
  | 'highest_rated'
  | 'quickest'
  | 'recently_made'
  | 'newest';

// =============================================================================
// Page Props (Inertia.js)
// =============================================================================

import type ApiFoodTag from './serializers/ApiFoodTag';
import type ApiFoodRecipe from './serializers/ApiFoodRecipe';
import type ApiFoodRecipeDetail from './serializers/ApiFoodRecipeDetail';
import type ApiFoodIngredient from './serializers/ApiFoodIngredient';
import type ApiFoodIngredientList from './serializers/ApiFoodIngredientList';
import type ApiFoodFamilyMember from './serializers/ApiFoodFamilyMember';

/**
 * Props for the Recipes Index page
 */
export interface RecipesIndexProps {
  recipes: ApiFoodRecipe[];
  tags: ApiFoodTag[];
  filters: RecipeFilters;
}

/**
 * Props for the Recipe Show page
 */
export interface RecipeShowProps {
  recipe: ApiFoodRecipeDetail;
  family_members: ApiFoodFamilyMember[];
}

/**
 * Props for the Recipe Form page (new/edit)
 */
export interface RecipeFormProps {
  recipe: ApiFoodRecipeDetail | null;
  ingredients: ApiFoodIngredient[];
  tags: ApiFoodTag[];
  family_members: ApiFoodFamilyMember[];
  errors?: Record<string, string[]>;
}

/**
 * Props for the Ingredients Index page
 */
export interface IngredientsIndexProps {
  ingredients: ApiFoodIngredientList[];
  categories: string[];
}

// =============================================================================
// Namespace Styling
// =============================================================================

/**
 * Tag namespace styling configuration
 */
export const TAG_NAMESPACE_STYLES: Record<string, { bg: string; text: string; activeBg: string }> = {
  diet: { bg: 'bg-green-100', text: 'text-green-800', activeBg: 'bg-green-600' },
  origin: { bg: 'bg-amber-100', text: 'text-amber-800', activeBg: 'bg-amber-600' },
  meal: { bg: 'bg-blue-100', text: 'text-blue-800', activeBg: 'bg-blue-600' },
  course: { bg: 'bg-cyan-100', text: 'text-cyan-800', activeBg: 'bg-cyan-600' },
  season: { bg: 'bg-orange-100', text: 'text-orange-800', activeBg: 'bg-orange-600' },
  other: { bg: 'bg-purple-100', text: 'text-purple-800', activeBg: 'bg-purple-600' },
};

/**
 * Get styling for a tag based on namespace
 */
export function getTagStyle(namespace: string | null | undefined): { bg: string; text: string; activeBg: string } {
  return TAG_NAMESPACE_STYLES[namespace || 'other'] || TAG_NAMESPACE_STYLES.other;
}

// =============================================================================
// Ingredient Category Styling
// =============================================================================

/**
 * Category styling for ingredient badges
 */
export const INGREDIENT_CATEGORY_STYLES: Record<string, { bg: string; text: string }> = {
  vegetables: { bg: 'bg-green-100', text: 'text-green-800' },
  dairy: { bg: 'bg-amber-100', text: 'text-amber-800' },
  meat: { bg: 'bg-red-100', text: 'text-red-600' },
  grains: { bg: 'bg-amber-100', text: 'text-amber-800' },
  spices: { bg: 'bg-purple-100', text: 'text-purple-700' },
  seafood: { bg: 'bg-blue-100', text: 'text-blue-800' },
  fruits: { bg: 'bg-pink-100', text: 'text-pink-700' },
  pantry: { bg: 'bg-gray-200', text: 'text-gray-700' },
};

/**
 * Get styling for an ingredient category
 */
export function getCategoryStyle(category: string | null | undefined): { bg: string; text: string } {
  return INGREDIENT_CATEGORY_STYLES[category || 'pantry'] || INGREDIENT_CATEGORY_STYLES.pantry;
}

// =============================================================================
// Recipe Import Types (interim - will be replaced by Typelizer types)
// =============================================================================

export type {
  ExtractSourceType,
  ExtractRequest,
  ExtractResponse,
  ImportRequest,
  ImportResponse,
  ExtractedRecipe,
  ExtractedIngredient,
  ImportIngredient,
  ImportIngredientExisting,
  ImportIngredientNew,
  RecipeImportPageProps,
} from './recipeImport';
